import React, { useEffect, useState, useMemo } from 'react';
import { 
  AlertTriangle, DollarSign, TrendingDown, TrendingUp, RotateCcw, 
  Clock, Activity, Target, Zap, Timer, Users, PieChart, BarChart3, 
  GitBranch, Bell, CheckCircle2, Play, Loader2, MousePointer2, Percent, Gauge, Layers,
  ThumbsUp, Lightbulb, Sparkles, ChevronDown, ChartNoAxesColumn
} from 'lucide-react';
import { simulateProcess, SimulationResult } from '../utils/simulation';
import { useProjectStore } from '../store/useProjectStore';
import { useTheme } from '../context/ThemeContext';
import { CardData } from '../types';
import { MiniFlowHeatmap } from './MiniFlowHeatmap';
import { COLORS, COLORS_DARK } from './DashboardPanel/constants';
import { formatTP, formatTRA } from './DashboardPanel/formatters';
import { IndicatorCard, KPICardCompact } from './DashboardPanel/KPICards';
import { ResourcePareto } from './DashboardPanel/ResourcePareto';
import { YamazumiChart } from './DashboardPanel/YamazumiChart';
import { GanttChart } from './DashboardPanel/GanttChart';
import { getRoleDailyCapacityByName } from '../utils/orgShiftUtils';
import { FutureStateModal } from './FutureStateModal';
import { ComparisonView } from './ComparisonView';

interface StrategicInsightCard {
  id: 'critical' | 'opportunity' | 'risk';
  badge: string;
  title: string;
  tone: 'rose' | 'amber' | 'slate';
  body: React.ReactNode;
  suggestion: React.ReactNode;
  focusResource?: string;
}

export const DashboardPanel = ({
    metrics,
    cards,
    onClose,
    setHighlight,
    dashboardMode = 'sidebar',
    onClearMapFilters,
    filterResetToken = 0,
    onSimulateSolution,
}: DashboardPanelProps) => {
    const isFullscreen = dashboardMode === 'fullscreen';
    const [selectedResp, setSelectedResp] = useState<string | null>(null);
    const [showFutureModal, setShowFutureModal] = useState(false);
    const [futureSimulation, setFutureSimulation] = useState<ReturnType<typeof useProjectStore.getState>['simulateFutureState'] | null>(null);
    const [showComparisonModal, setShowComparisonModal] = useState(false);
    
    const { isDark } = useTheme();
    const colors = isDark ? COLORS_DARK : COLORS;
    
    const heatmapMode = useProjectStore(state => state.heatmapMode);
    const setHeatmapMode = useProjectStore(state => state.setHeatmapMode);
    const simulateFutureState = useProjectStore(state => state.simulateFutureState);
    const getFutureMetrics = useProjectStore(state => state.getFutureMetrics);
    const isKaizenMode = useProjectStore(state => state.isKaizenMode);
    const organization = useProjectStore(state => state.organization);
    const mapType = useProjectStore(state => state.mapType);
    const globalDemand = useProjectStore(state => state.globalDemand);
    const factoryWorkDays = useProjectStore(state => state.factoryWorkDays);
    const viewMode = useProjectStore(state => state.viewMode);
    const postIts = useProjectStore(state => state.postIts);
    const isFactoryMap = mapType === 'FACTORY';
    const isFuturo = viewMode === 'future_simulated';
    const isKaizenWork = viewMode === 'kaizen_work';

    const handleGenerateFuture = () => {
        const simulation = simulateFutureState();
        setFutureSimulation(simulation);
        setShowFutureModal(true);
    };

    const handleShowComparison = () => {
        setShowComparisonModal(true);
    };

    useEffect(() => {
        setSelectedResp(null);
        setHighlight(null);
    }, [filterResetToken, setHighlight]);

    const normalizeText = (value: string | undefined | null) =>
        (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

    const handleSimulateInsightSolution = (focusResource?: string) => {
        const suggestedPayload = {
            focusResource,
            suggestedExtraFte: 1,
            suggestedWaitReductionPercent: 20,
        };

        if (focusResource) {
            const normalizedFocus = normalizeText(focusResource);
            const relatedIds = cards
                .filter((card) => {
                    if (card.status === 'removido' || card.status === 'deletado') return false;
                    const resourceLabel = normalizeText(card.quem || card.responsavel || card.atividade || '');
                    return resourceLabel.includes(normalizedFocus) || normalizedFocus.includes(resourceLabel);
                })
                .map((card) => card.id);

            setSelectedResp(focusResource);
            setHighlight(relatedIds.length > 0 ? relatedIds : null);
        }

        if (onSimulateSolution) {
            onSimulateSolution(suggestedPayload);
            return;
        }

        // Fallback legado: mantm fluxo existente se no houver integrao com a sidebar.
        const simulation = simulateFutureState();
        setFutureSimulation(simulation);
        setShowFutureModal(false);
        handleShowComparison();
    };

    // ---------------------------------------------------------
    // USAR MÉTRICAS JÁ CALCULADAS (metrics vem de calculateMetrics)
    // ---------------------------------------------------------
    const dashboardMetrics = useMemo(() => {
        // Função auxiliar para garantir número válido (evita NaN/Infinity)
        const safeNumber = (val: any, fallback = 0): number => {
            const num = Number(val);
            return (Number.isFinite(num) && !Number.isNaN(num)) ? num : fallback;
        };
        
        // Constante para conversão de dias úteis
        const MINUTES_PER_WORKING_DAY = 480; // 8 horas = 480 minutos
        
        // Flags de overflow vindos de calculations.ts
        const hasOverflowCost = metrics?.hasOverflowCost || false;
        const hasOverflowLeadTime = metrics?.hasOverflowLeadTime || false;
        
        // Usar dados já calculados corretamente em calculations.ts
        const longestPathMinutes = safeNumber(metrics?.longestPath);
        const totalTT = safeNumber(metrics?.totalTT);
        const totalTP = safeNumber(metrics?.totalLTSum);
        const efficiency = safeNumber(metrics?.flowEfficiency);
        const cost = hasOverflowCost ? 0 : safeNumber(metrics?.monthlyOpCost);
        
        // Lead Time em dias úteis (1 dia = 480 min, não 1440)
        const leadTimeInDays = safeNumber(totalTP / MINUTES_PER_WORKING_DAY);
        const leadTimeFormatted = metrics?.leadTimeFormatted || `${totalTP.toFixed(0)} min`;
        
        // Converter resourceRanking se existir
        const resourceRanking = (metrics?.resourceRanking || []).map((r: any) => {
            const dailyCap = getRoleDailyCapacityByName(organization, r.cargo);
            return {
                name: r.cargo || 'No Atribudo',
                total: r.tp || 0,
                tra: r.tra || 0,
                wait: r.espera || 0,
                efficiency: r.tp > 0 ? (r.tra / r.tp) * 100 : 0,
                capacity: dailyCap
            };
        });
        
        // ===============================================
        // NOVOS CÁLCULOS: DIMENSIONAMENTO LEAN
        // ===============================================
        
        // 1. CAPTURA DE DEMANDA DO CARD INICIAL
        const startCard = cards.find(c => c.tipo === 'card_inicial');
        const startCardUnit: string = (startCard as any)?.unidadeDemanda || '/dia';
        const rawEntrada = Number(startCard?.entrada) || 0;
        // Normalizar para unidades/dia independente da unidade configurada
        const toDailyDemand = (val: number, unit: string): number => {
          if (unit === '/hora') return val * 8;   // 8h teis/dia
          if (unit === '/ms')  return val / 20;  // 20 dias teis/ms
          return val; // /dia
        };
        const dailyDemand = toDailyDemand(rawEntrada, startCardUnit);
        
        // Constantes de Negócio
        const availableTime = 480; // min por turno
        const workingDays = 20; // dias úteis/mês
        
        // 2. TAKT TIME (Ritmo do Cliente)
        // Quanto tempo tenho para produzir 1 unidade?
        const taktTime = dailyDemand > 0 ? availableTime / dailyDemand : 0;
        
        // 3. CYCLE TIME DO GARGALO
        const bottleneckCycleTime = resourceRanking.length > 0 ? resourceRanking[0].total : 0;
        
        // 4. STATUS DE CAPACIDADE
        // Se Cycle > Takt, não conseguimos atender a demanda
        const canMeetDemand = bottleneckCycleTime > 0 && taktTime > 0 
            ? bottleneckCycleTime <= taktTime 
            : true;
        
        // 5. FTE REQUERIDO (Pessoas Necessárias)
        // Fórmula: (Demanda * Tempo de Trabalho Total) / Tempo Disponível
        const fteRequired = dailyDemand > 0 && totalTT > 0
            ? (dailyDemand * totalTT) / availableTime
            : 0;
        
        // 6. FTE ATUAL (Contar responsáveis únicos)
        const uniqueResponsibles = new Set(
            cards
                .filter(c => c.responsavel && c.responsavel.trim() !== '')
                .map(c => c.responsavel)
        );
        const fteActual = uniqueResponsibles.size;
        
        // 7. SAVING POTENCIAL (Economia ao eliminar espera)
        // Premissa: R$ 50/hora como custo médio de mão de obra
        const hourlyRate = 50; // R$/hora
        const totalWaitMinutes = totalTP - totalTT;
        
        // CORRE!ÑO: Saving anual = Tempo de espera por unidade  dias úteis  12 meses  custo/hora
        // Cada unidade passa UMA VEZ pelo fluxo, não multiplicamos pela demanda diária
        // O custo é: tempo_espera_por_unidade  unidades_ano  custo_por_minuto
        const unitsPerYear = dailyDemand * workingDays * 12;
        const savingFull = totalWaitMinutes > 0 
            ? (totalWaitMinutes * unitsPerYear * (hourlyRate / 60))
            : 0;
        
        // Meta realista: 50% de redução da espera
        const savingRealistic = savingFull * 0.5;
        
        // ===============================================
        // RTY (ROLLED THROUGHPUT YIELD) - First Pass Yield
        // ===============================================
        // Probabilidade de um item passar pelo fluxo inteiro sem erro
        // RTY = Æ (Assertividade de cada etapa)
        const activityCards = cards.filter(c => 
            c.tipo === 'atividade' && c.status === 'ativo'
        );
        
        let rty = 100; // 100% se não houver dados
        if (activityCards.length > 0) {
            // Calcular produto das assertividades (cada uma como fração)
            const yieldProduct = activityCards.reduce((acc, card) => {
                const assertividade = card.assertividade ?? 98; // Default 98%
                return acc * (assertividade / 100);
            }, 1);
            rty = yieldProduct * 100;
        }
        
        // ===============================================
        // CUSTO DO DESPERDÍCIO (Monetização do NVA)
        // ===============================================
        // Custo diário = Tempo de espera (NVA)  Custo/hora  Demanda
        // PROTE!ÑO: Evitar cálculos com dados inválidos
        const waitCostPerItem = totalWaitMinutes > 0 ? (totalWaitMinutes / 60) * hourlyRate : 0;
        const dailyWasteCost = dailyDemand > 0 ? safeNumber(waitCostPerItem * dailyDemand) : 0;
        const monthlyWasteCost = safeNumber(dailyWasteCost * workingDays);
        
        // ===============================================
        // ANLISE DE BALANCEAMENTO (Takt-based)
        // ===============================================
        // Verificar se QUALQUER card ativo tem TRA > Takt Time
        const activeActivityCards = cards.filter(c => 
            c.tipo === 'atividade' && 
            c.status !== 'removido' &&
            Number(c.tt) > 0
        );
        
        // Encontrar todos os cards que excedem o Takt
        const cardsExceedingTakt = taktTime > 0 
            ? activeActivityCards.filter(c => Number(c.tt) > taktTime)
            : [];
        
        // Fluxo é balanceado se NENHUM card excede o Takt
        const isFlowBalanced = cardsExceedingTakt.length === 0;
        
        // Identificar o maior gargalo (card com maior excedente sobre Takt)
        const bottleneckCard = cardsExceedingTakt.length > 0
            ? cardsExceedingTakt.reduce((max, card) => 
                Number(card.tt) > Number(max.tt) ? card : max
              )
            : null;
        
        // Nome do gargalo para exibição
        const bottleneckName = bottleneckCard 
            ? (bottleneckCard.quem || bottleneckCard.responsavel || bottleneckCard.atividade || 'Não identificado')
            : null;
        
        // Excedente percentual do gargalo
        const bottleneckExceedPercent = bottleneckCard && taktTime > 0
            ? ((Number(bottleneckCard.tt) / taktTime - 1) * 100)
            : 0;
        
        // Verificar ociosidade excessiva (todos TRA < 50% do Takt)
        const hasExcessiveIdleness = taktTime > 0 && activeActivityCards.length > 0 &&
            activeActivityCards.every(c => Number(c.tt) < taktTime * 0.5);
        
        // ===============================================
        // DADOS PARA YAMAZUMI (por 1 unidade)
        // ===============================================
        // Mostra tempo por ITEM (não soma diária)
        // O resourceRanking já tem valores ponderados pelo flow
        // Takt Time é a referência (tempo disponível por item)
        const yamazumiData = resourceRanking.map((r: any) => {
            const trabalho = r.tra || 0; // TRA por item
            const espera = r.wait || 0; // Espera por item
            return {
                name: r.name,
                trabalho,
                espera,
                disponibilidade: taktTime, // Takt = tempo disponível por item
                ocupacao: taktTime > 0 ? safeNumber((trabalho + espera) / taktTime * 100) : 0
            };
        });
        
        // Garantir todos os valores numéricos são válidos no retorno
        return {
            totalTP: safeNumber(totalTP),
            totalTT: safeNumber(totalTT),
            totalWait: safeNumber(totalTP - totalTT),
            efficiency: safeNumber(efficiency),
            cost: safeNumber(cost),
            flowVelocity: totalTT > 0 ? safeNumber(480 / totalTT) : 0,
            oee: safeNumber(efficiency * 0.95),
            resourceRanking,
            // Novos campos de dimensionamento
            dailyDemand: safeNumber(dailyDemand),
            taktTime: safeNumber(taktTime),
            bottleneckCycleTime: safeNumber(bottleneckCycleTime),
            canMeetDemand,
            fteRequired: safeNumber(fteRequired),
            fteActual,
            savingRealistic: safeNumber(savingRealistic),
            // Novos KPIs Executivos
            rty: safeNumber(rty),
            dailyWasteCost: safeNumber(dailyWasteCost),
            monthlyWasteCost: safeNumber(monthlyWasteCost),
            yamazumiData,
            hourlyRate,
            // Análise de Balanceamento
            isFlowBalanced,
            cardsExceedingTakt: cardsExceedingTakt.length,
            bottleneckName,
            bottleneckExceedPercent: safeNumber(bottleneckExceedPercent),
            hasExcessiveIdleness,
            // Flags de overflow e Lead Time formatado
            hasOverflowCost,
            hasOverflowLeadTime,
            leadTimeInDays: safeNumber(leadTimeInDays),
            leadTimeFormatted
        };
    }, [metrics, cards, organization]);

    const factoryMetrics = useMemo(() => {
        const safeNumber = (val: any, fallback = 0): number => {
            const num = Number(val);
            return (Number.isFinite(num) && !Number.isNaN(num)) ? num : fallback;
        };
        const toSeconds = (value: any, unit?: string, fallbackUnit: 's' | 'min' | 'h' = 's'): number => {
            const num = safeNumber(value);
            if (!num) return 0;
            const resolvedUnit = unit || fallbackUnit;

            if (['dias', 'dia', 'd'].includes(resolvedUnit)) return num * 86400;
            if (['horas', 'hora', 'h'].includes(resolvedUnit)) return num * 3600;
            if (['minutos', 'minuto', 'min'].includes(resolvedUnit)) return num * 60;
            return num;
        };
        const toMinutes = (value: any, unit?: string, fallbackUnit: 's' | 'min' | 'h' = 's'): number =>
            toSeconds(value, unit, fallbackUnit) / 60;

        const activeCards = cards.filter(card => !['removido', 'deletado'].includes(card.status ?? ''));
        const processCards = activeCards.filter(card => card.tipo === 'atividade' && (card as any).vsmIcon === 'processo');
        const inventoryCards = activeCards.filter(card => card.tipo === 'inventory');
        const externalCards = activeCards.filter(card => ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? ''));
        const communicationCards = activeCards.filter(card => (card as any).vsmIcon === 'comunicacao');
        const transportCards = activeCards.filter(card => ['caminhao', 'transporte'].includes((card as any).vsmIcon ?? ''));

        const clientCard = externalCards.find(card => {
            const extType = String((card as any).extType ?? '').toUpperCase();
            return card.tipo === 'card_final' || extType === 'CLIENTE' || (card as any).vsmIcon === 'cliente';
        });
        const getDailyDemand = (card: any): number => {
            const explicitDailyDemand = safeNumber(card?.vsmDemanda ?? card?.entrada);
            const demandUnit = String(card?.unidadeDemanda ?? card?.vsmDemandaUnidade ?? '/dia').toLowerCase();
            if (explicitDailyDemand > 0) {
                if (demandUnit.includes('mês') || demandUnit.includes('mes') || demandUnit.includes('/ms')) return explicitDailyDemand / (safeNumber(card?.vsmDiasMes, factoryWorkDays || 20) || 20);
                if (demandUnit.includes('hora')) return explicitDailyDemand * 8;
                return explicitDailyDemand;
            }

            const monthlyMix = Array.isArray(card?.vsmDemandMix) ? card.vsmDemandMix : [];
            const monthlyTotal = monthlyMix.reduce((sum: number, item: any) => sum + safeNumber(item?.qty), 0);
            const workDays = safeNumber(card?.vsmDiasMes, factoryWorkDays || 20);
            return monthlyTotal > 0 && workDays > 0 ? monthlyTotal / workDays : 0;
        };
        const scenarioDemand = 920;
        const originalLeadTimeDays = 23.6;
        const originalVaSeconds = 166;
        const originalCapacityPerDay = 464;
        const originalBottleneckName = 'Montagem I';
        const originalBottleneckCycleSeconds = 28800 / originalCapacityPerDay;
        const dailyDemand = isFuturo ? scenarioDemand : getDailyDemand(clientCard) || globalDemand || scenarioDemand;

        const getLinkedCycleReductionPercent = (cardId: string): number => {
            return postIts.reduce((maxReduction, postIt) => {
                if (postIt.type !== 'action') return maxReduction;
                const directImpactReduction = postIt.impactos?.reduce((impactMax, impact) => {
                    if (impact.targetNodeId !== cardId || !['TRA', 'TP', 'ELIMINAR'].includes(impact.tipo)) return impactMax;
                    return Math.max(impactMax, impact.tipo === 'ELIMINAR' ? 100 : safeNumber(impact.reducao));
                }, 0) ?? 0;
                return Math.max(maxReduction, directImpactReduction);
            }, 0);
        };

        const getProjectedCycleSeconds = (card: CardData): number => {
            const baselineSeconds = toSeconds((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit ?? card.tpUnit, 's');
            const projectionSeconds = safeNumber(
                (card as any).vsmFlowCellProjectedCycleSeconds
                ?? (card as any).vsmKaizenProjection?.metrics?.cycleTime?.projectedSeconds
                ?? (card as any).vsmKaizenProjection?.projectedSeconds,
            );
            if (projectionSeconds > 0) return projectionSeconds;

            const reductionPercent = Math.max(0, Math.min(100, getLinkedCycleReductionPercent(card.id)));
            return baselineSeconds > 0 ? baselineSeconds * (1 - reductionPercent / 100) : 0;
        };

        const cycleTimes = processCards.map(card => toMinutes((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit, 's')).filter(value => value > 0);
        const totalCycleTime = cycleTimes.reduce((sum, value) => sum + value, 0);
        const bottleneckCycleTime = cycleTimes.length ? Math.max(...cycleTimes) : 0;
        const bottleneckCard = processCards.find(card => toMinutes((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit, 's') === bottleneckCycleTime);
        const getInventoryDays = (card: CardData): number => {
            if (isKaizenWork && ((card as any).blocked === true || (card as any).vsmFlowCellBlocked === true)) return 0;

            const fixedBufferDays = safeNumber((card as any).vsmFixedLeadTimeDays ?? (card as any).vsmBufferDays);
            if (isKaizenWork && fixedBufferDays > 0) return fixedBufferDays;

            const manualDays = (card as any).vsmDias;
            if (manualDays != null && manualDays !== '') return safeNumber(manualDays);

            const inventory = safeNumber((card as any).inventory);
            return dailyDemand > 0 ? inventory / dailyDemand : 0;
        };
        const getInventoryUnits = (card: CardData): number => {
            if (isKaizenWork && ((card as any).blocked === true || (card as any).vsmFlowCellBlocked === true)) return 0;
            const days = getInventoryDays(card);
            if (days > 0 && dailyDemand > 0) return days * dailyDemand;
            return safeNumber((card as any).inventory);
        };
        const inventoryDays = inventoryCards.reduce((sum, card) => sum + getInventoryDays(card), 0);
        const inventoryUnits = inventoryCards.reduce((sum, card) => sum + getInventoryUnits(card), 0);
        const inventoryBreakdown = inventoryCards
            .map((card, index) => {
                const inventory = getInventoryUnits(card);
                const days = getInventoryDays(card);
                return {
                    name: card.atividade && card.atividade !== 'Estoque' ? card.atividade : `Estoque ${index + 1}`,
                    days,
                    units: inventory,
                };
            })
            .sort((a, b) => b.days - a.days);
        const processDays = totalCycleTime * 60 / 86400;
        const leadTimeDays = inventoryDays + processDays;
        const pce = leadTimeDays > 0 ? (processDays / leadTimeDays) * 100 : 0;
        const taktTime = isFuturo ? 0.5 : dailyDemand > 0 ? 480 / dailyDemand : dashboardMetrics.taktTime;
        const availabilityValues = processCards.map(card => safeNumber((card as any).vsmDisp ?? card.assertividade, 100)).filter(value => value > 0);
        const avgAvailability = availabilityValues.length
            ? availabilityValues.reduce((sum, value) => sum + value, 0) / availabilityValues.length
            : 100;
        const rty = availabilityValues.length
            ? availabilityValues.reduce((product, value) => product * (value / 100), 1) * 100
            : dashboardMetrics.rty;
        const capacityPerDay = bottleneckCycleTime > 0 ? Math.floor(480 / bottleneckCycleTime) : 0;
        const canMeetDemand = taktTime > 0 && bottleneckCycleTime > 0 ? bottleneckCycleTime <= taktTime : true;
        const bottleneckGapPercent = taktTime > 0 && bottleneckCycleTime > taktTime ? ((bottleneckCycleTime / taktTime) - 1) * 100 : 0;
        const kaizenCycleTimes = processCards.map(card => getProjectedCycleSeconds(card) / 60).filter(value => value > 0);
        const kaizenTotalCycleTime = kaizenCycleTimes.reduce((sum, value) => sum + value, 0);
        const kaizenBottleneckCycleTime = kaizenCycleTimes.length ? Math.max(...kaizenCycleTimes) : bottleneckCycleTime;
        const kaizenBottleneckCard = processCards.find(card => getProjectedCycleSeconds(card) / 60 === kaizenBottleneckCycleTime);
        const factoryYamazumiData = processCards.map(card => {
            const trabalho = isKaizenWork ? getProjectedCycleSeconds(card) / 60 : toMinutes((card as any).vsmCT ?? card.tp, (card as any).vsmCTUnit, 's');
            const ocupacao = taktTime > 0 ? (trabalho / taktTime) * 100 : 0;
            return {
                name: card.atividade || 'Processo',
                trabalho,
                espera: 0,
                disponibilidade: safeNumber((card as any).vsmDisp ?? card.assertividade, 100),
                ocupacao,
            };
        });

        const futureInventoryBreakdown = [
            { name: 'Supermercado Matéria-Prima', days: 1.5, units: 0 },
            { name: 'Pulmão Intermediário', days: 1.0, units: 0 },
            { name: 'Supermercado Expedição', days: 1.5, units: 0 },
        ];
        const futureYamazumiData = [
            { name: 'Estamparia', trabalho: 1 / 60, espera: 0, disponibilidade: 100, ocupacao: taktTime > 0 ? ((1 / 60) / taktTime) * 100 : 0 },
            { name: 'Célula Solda+Montagem', trabalho: 18.3 / 60, espera: 0, disponibilidade: 100, ocupacao: taktTime > 0 ? ((18.3 / 60) / taktTime) * 100 : 0 },
        ];
        const futureVaSeconds = 56;
        const kaizenTotalCycleSeconds = kaizenTotalCycleTime * 60 || originalVaSeconds;
        const kaizenInventoryDays = dailyDemand > 0 ? inventoryUnits / dailyDemand : inventoryDays;
        const kaizenLeadTimeDays = Math.max(0, kaizenInventoryDays + kaizenTotalCycleSeconds / 86400);
        const kaizenBottleneckCycleSeconds = kaizenBottleneckCycleTime * 60 || originalBottleneckCycleSeconds;
        const kaizenCapacityPerDay = kaizenBottleneckCycleSeconds > 0 ? Math.floor(28800 / kaizenBottleneckCycleSeconds) : originalCapacityPerDay;
        const scenarioLeadTimeDays = isFuturo ? 4.0 : isKaizenWork ? kaizenLeadTimeDays : originalLeadTimeDays;
        const scenarioVaSeconds = isFuturo ? futureVaSeconds : isKaizenWork ? kaizenTotalCycleSeconds : originalVaSeconds;
        const scenarioTotalCycleTime = scenarioVaSeconds / 60;
        const scenarioInventoryDays = Math.max(0, scenarioLeadTimeDays - scenarioVaSeconds / 86400);
        const scenarioPce = isFuturo ? 0.16 : (scenarioVaSeconds / (scenarioLeadTimeDays * 86400)) * 100;
        const scenarioCapacityPerDay = isFuturo ? 1571 : isKaizenWork ? kaizenCapacityPerDay : originalCapacityPerDay;
        const scenarioBottleneckCycleTime = isFuturo ? 18.3 / 60 : isKaizenWork ? kaizenBottleneckCycleSeconds / 60 : originalBottleneckCycleSeconds / 60;
        const scenarioCanMeetDemand = isFuturo ? true : scenarioBottleneckCycleTime <= taktTime;
        const scenarioGapPercent = taktTime > 0 && scenarioBottleneckCycleTime > taktTime ? ((scenarioBottleneckCycleTime / taktTime) - 1) * 100 : 0;
        const scenarioInventoryBreakdown = isFuturo ? futureInventoryBreakdown : inventoryBreakdown;
        const leadTimeDeltaDays = scenarioLeadTimeDays - originalLeadTimeDays;
        const capacityDeltaPerDay = scenarioCapacityPerDay - originalCapacityPerDay;

        return {
            processCount: isFuturo ? 2 : processCards.length,
            inventoryCount: isFuturo ? 3 : inventoryCards.length,
            communicationCount: communicationCards.length,
            transportCount: transportCards.length,
            dailyDemand,
            taktTime,
            totalCycleTime: scenarioTotalCycleTime,
            bottleneckCycleTime: scenarioBottleneckCycleTime,
            bottleneckName: isFuturo ? 'Célula Solda+Montagem' : isKaizenWork ? kaizenBottleneckCard?.atividade || originalBottleneckName : originalBottleneckName,
            leadTimeDays: scenarioLeadTimeDays,
            inventoryDays: isFuturo ? 4.0 : scenarioInventoryDays,
            inventoryUnits: isFuturo ? 0 : inventoryUnits,
            inventoryBreakdown: scenarioInventoryBreakdown,
            pce: scenarioPce,
            avgAvailability: isFuturo ? 100 : avgAvailability,
            rty,
            capacityPerDay: scenarioCapacityPerDay,
            canMeetDemand: scenarioCanMeetDemand,
            bottleneckGapPercent: scenarioGapPercent,
            capacityAvailablePercent: isFuturo ? 70 : Math.max(0, Math.round((scenarioCapacityPerDay / dailyDemand - 1) * 100)),
            factoryYamazumiData: isFuturo ? futureYamazumiData : factoryYamazumiData,
            leadTimeDeltaDays,
            capacityDeltaPerDay,
            originalLeadTimeDays,
            originalCapacityPerDay,
            isOptimizationActive: isKaizenWork,
        };
    }, [cards, dashboardMetrics.rty, dashboardMetrics.taktTime, factoryWorkDays, globalDemand, isFuturo, isKaizenWork, postIts]);

    const formatFactoryPce = (value: number) => {
        if (value > 0 && value < 1) return value.toFixed(2);
        return value.toFixed(1);
    };
    const formatFactoryVa = (cycleMinutes: number) => {
        const seconds = cycleMinutes * 60;
        return seconds < 120 ? `${Math.round(seconds)}s` : `${cycleMinutes.toFixed(1)}m`;
    };
    const formatFactoryCycle = (cycleMinutes: number) => {
        const seconds = cycleMinutes * 60;
        return cycleMinutes < 1 ? `${seconds.toFixed(1)}s` : `${cycleMinutes.toFixed(1)} min`;
    };
    const showFactoryImpact = isFactoryMap && isKaizenWork;
    const renderImpactBadge = (delta: number, unit: string, improvementWhen: 'increase' | 'decrease', precision = 1) => {
        if (!showFactoryImpact || Math.abs(delta) < 0.05) return null;
        const isImprovement = improvementWhen === 'increase' ? delta > 0 : delta < 0;
        const formattedDelta = precision === 0 ? Math.round(delta).toLocaleString('pt-BR') : delta.toFixed(precision);
        return (
            <span className={`shrink-0 rounded-sm px-1.5 py-0.5 text-[9px] font-bold ${isImprovement ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'}`}>
                {delta > 0 ? '+' : ''}{formattedDelta}{unit}
            </span>
        );
    };

    // ---------------------------------------------------------
    // SIMULA!ÑO MONTE CARLO REAL (10.000 iterações)
    // ---------------------------------------------------------
    const monteCarloResult = useMemo<SimulationResult | null>(() => {
        if (!cards || cards.length < 2) return null;
        try {
            // Usar 10.000 iterações com variação de Â±20%
            return simulateProcess(cards, 10000, -20, 20);
        } catch (e) {
            console.warn('[DashboardPanel] Erro na simulação Monte Carlo:', e);
            return null;
        }
    }, [cards]);

    // ---------------------------------------------------------
    // CONSULTOR LEAN ATIVO (INSIGHTS ESTRAT0GICOS)
    // ---------------------------------------------------------
    const strategicInsights = useMemo<StrategicInsightCard[]>(() => {
        const insights: StrategicInsightCard[] = [];

        if (isFactoryMap) {
            if (!factoryMetrics.canMeetDemand && factoryMetrics.taktTime > 0) {
                insights.push({
                    id: 'critical',
                    badge: 'CAPACIDADE',
                    title: 'Gargalo acima do Takt',
                    tone: 'rose',
                    focusResource: factoryMetrics.bottleneckName,
                    body: (
                        <>
                            <span className="font-semibold">{factoryMetrics.bottleneckName}</span> está em{' '}
                            <span className="font-mono font-semibold">{factoryMetrics.bottleneckCycleTime.toFixed(1)} min</span> contra takt de{' '}
                            <span className="font-mono font-semibold">{factoryMetrics.taktTime.toFixed(1)} min</span>.
                        </>
                    ),
                    suggestion: <>Sugestão: balancear operação, reduzir CT do gargalo ou revisar demanda/capacidade diária.</>,
                });
            }

            const worstInventory = factoryMetrics.inventoryBreakdown[0];
            if (worstInventory && worstInventory.days > 0) {
                insights.push({
                    id: 'opportunity',
                    badge: 'ESTOQUE',
                    title: 'Maior contribuição para Lead Time',
                    tone: 'amber',
                    focusResource: worstInventory.name,
                    body: (
                        <>
                            <span className="font-semibold">{worstInventory.name}</span> concentra{' '}
                            <span className="font-mono font-semibold">{worstInventory.days.toFixed(1)} dias</span> de espera no fluxo.
                        </>
                    ),
                    suggestion: <>Sugestão: atacar supermercado, lote, frequência de abastecimento ou política de reposição desse ponto.</>,
                });
            }

            if (factoryMetrics.avgAvailability < 90) {
                insights.push({
                    id: 'risk',
                    badge: 'DISPONIBILIDADE',
                    title: 'Estabilidade produtiva baixa',
                    tone: 'slate',
                    body: (
                        <>
                            Disponibilidade média em <span className="font-mono font-semibold">{factoryMetrics.avgAvailability.toFixed(1)}%</span> reduz a previsibilidade do fluxo.
                        </>
                    ),
                    suggestion: <>Sugestão: priorizar paradas recorrentes, setup e perdas de disponibilidade nos processos críticos.</>,
                });
            }

            if (insights.length === 0) {
                insights.push({
                    id: 'risk',
                    badge: 'ESTABILIDADE',
                    title: 'Fluxo sob Controle',
                    tone: 'slate',
                    body: <>Sem desvios críticos nos indicadores Factory atuais.</>,
                    suggestion: <>Sugestão: manter acompanhamento de takt, CT gargalo, estoque em dias e disponibilidade.</>,
                });
            }

            return insights.slice(0, 3);
        }

        const activities = cards
            .filter((c) => c.tipo === 'atividade' && c.status !== 'removido')
            .sort((a: any, b: any) => {
                const ax = Number(a?.posicao?.x ?? 0);
                const bx = Number(b?.posicao?.x ?? 0);
                return ax - bx;
            });

        const nodeA = activities[0]?.atividade || activities[0]?.quem || activities[0]?.responsavel || 'N A';
        const nodeB = activities[1]?.atividade || activities[1]?.quem || activities[1]?.responsavel || 'N B';

        const bottleneck = dashboardMetrics.resourceRanking?.[0];
        const bottleneckLoad = Number(bottleneck?.total ?? 0);
        const occupationPct = dashboardMetrics.taktTime > 0 ? (bottleneckLoad / dashboardMetrics.taktTime) * 100 : 0;
        if (bottleneck && occupationPct > 100) {
            const leadTimeDays = Number(dashboardMetrics.leadTimeInDays ?? 0);
            const overloadFactor = Math.max(0, occupationPct / 100 - 1);
            const impactDays = Math.max(0.1, leadTimeDays * overloadFactor * 0.35);
            const retrabalhoPct = Math.min(35, Math.max(8, Math.round(overloadFactor * 30)));
            insights.push({
                id: 'critical',
                badge: 'MURI / SOBRECARGA',
                title: 'Gargalo Estrutural',
                tone: 'rose',
                focusResource: bottleneck.name,
                body: (
                    <>
                        Gargalo estrutural detectado em <span className="font-semibold">{bottleneck.name}</span>. Impacto:
                        aumento de <span className="font-mono font-semibold"> {impactDays.toFixed(1)} dias</span> no Lead Time.
                    </>
                ),
                suggestion: (
                    <>
                        Sugestão: testar <span className="font-mono font-semibold">+1 FTE</span> ou reduzir retrabalho em
                        <span className="font-mono font-semibold"> {retrabalhoPct}%</span>.
                    </>
                ),
            });
        }

        const waitRatio = dashboardMetrics.totalTP > 0 ? dashboardMetrics.totalWait / dashboardMetrics.totalTP : 0;
        if (dashboardMetrics.totalWait > 0 && (waitRatio >= 0.35 || dashboardMetrics.savingRealistic > 0)) {
            insights.push({
                id: 'opportunity',
                badge: 'MUDA / ROI',
                title: 'Oportunidade de Destravamento de Caixa',
                tone: 'amber',
                focusResource: bottleneck?.name,
                body: (
                    <>
                        Reduzir a espera em 50% gera ROI imediato de{' '}
                        <span className="font-mono font-semibold">
                            {dashboardMetrics.savingRealistic.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                maximumFractionDigits: 0,
                            })}
                        </span>.
                    </>
                ),
                suggestion: (
                    <>
                        Sugestão: avaliar o layout operacional entre{' '}
                        <span className="font-semibold">{nodeA}</span> e <span className="font-semibold">{nodeB}</span>.
                    </>
                ),
            });
        }

        const p10 = Number(monteCarloResult?.p10LeadTime ?? dashboardMetrics.totalTP * 0.75);
        const p50 = Number(monteCarloResult?.p50LeadTime ?? dashboardMetrics.totalTP);
        const p90 = Number(monteCarloResult?.p90LeadTime ?? dashboardMetrics.totalTP * 1.35);
        const stdDeviation = Number(monteCarloResult?.stdDeviation ?? Math.abs(p90 - p10) / 3);
        const spread = Math.max(0, p90 - p10);
        const variabilityHigh = stdDeviation > Math.max(8, p50 * 0.18);
        if (variabilityHigh) {
            insights.push({
                id: 'risk',
                badge: 'VARIABILIDADE',
                title: 'Baixa Previsibilidade de Entrega',
                tone: 'slate',
                focusResource: bottleneck?.name,
                body: (
                    <>
                        Há variação de <span className="font-mono font-semibold">{Math.round(spread)} min</span> entre
                        cenário otimista e pessimista.
                    </>
                ),
                suggestion: (
                    <>
                        Sugestão: estabilizar os tempos de{' '}
                        <span className="font-semibold">{dashboardMetrics.bottleneckName || bottleneck?.name || 'atividade crítica'}</span>.
                    </>
                ),
            });
        }

        if (insights.length === 0) {
            insights.push({
                id: 'risk',
                badge: 'ESTABILIDADE',
                title: 'Fluxo sob Controle',
                tone: 'slate',
                body: <>Sem desvios críticos no cenário atual. Monitorar variação de carga e espera em ciclos curtos.</>,
                suggestion: <>Sugestão: manter revisão diária dos indicadores para preservar a previsibilidade.</>,
            });
        }

        return insights.slice(0, 3);
    }, [cards, dashboardMetrics, factoryMetrics, isFactoryMap, monteCarloResult]);

    // ---------------------------------------------------------
    // RENDERIZA!ÑO: MODO SIDEBAR (Compacto - Restaurado)
    // ---------------------------------------------------------
    if (!isFullscreen) {
        return (
            <div className="h-full overflow-y-auto p-3 space-y-3" style={{ backgroundColor: colors.bgGeneral }}>
                {/* 1. ANÁLISE DE BALANCEAMENTO DO FLUXO */}
                {isFactoryMap && isKaizenWork ? (
                    <div 
                        className={`rounded p-2.5 shadow-sm ${factoryMetrics.canMeetDemand ? 'bg-emerald-50 border border-emerald-200' : ''}`}
                        style={factoryMetrics.canMeetDemand ? undefined : { 
                            backgroundColor: colors.redSoft,
                            borderLeft: `4px solid ${colors.redCritical}`
                        }}
                    >
                        <div className={`flex items-center gap-1.5 text-[10px] font-semibold mb-0.5 ${factoryMetrics.canMeetDemand ? 'text-emerald-600' : ''}`} style={factoryMetrics.canMeetDemand ? undefined : { color: colors.redCritical }}>
                            {factoryMetrics.canMeetDemand ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                            {factoryMetrics.canMeetDemand ? 'Gargalo Solucionado' : 'Otimização em Andamento'}
                        </div>
                        <div className="text-sm font-bold" style={{ color: colors.grayText }}>
                            {factoryMetrics.bottleneckName}
                        </div>
                        <div className="flex justify-between mt-1.5 text-xs" style={{ color: colors.grayText, opacity: 0.8 }}>
                            <span>CT gargalo: <b className={factoryMetrics.canMeetDemand ? 'text-emerald-600' : ''} style={factoryMetrics.canMeetDemand ? undefined : { color: colors.redCritical }}>{formatFactoryCycle(factoryMetrics.bottleneckCycleTime)}</b></span>
                        </div>
                        <div className="flex justify-between mt-0.5 text-xs" style={{ color: colors.grayText, opacity: 0.8 }}>
                            <span>Takt: <b>{factoryMetrics.taktTime.toFixed(1)} min</b></span>
                            <span>{factoryMetrics.canMeetDemand ? 'Status' : 'Excede'}: <b className={factoryMetrics.canMeetDemand ? 'text-emerald-600' : ''} style={factoryMetrics.canMeetDemand ? undefined : { color: colors.redCritical }}>{factoryMetrics.canMeetDemand ? 'OK' : `+${factoryMetrics.bottleneckGapPercent.toFixed(0)}%`}</b></span>
                        </div>
                    </div>
                ) : isFactoryMap && !factoryMetrics.canMeetDemand && factoryMetrics.taktTime > 0 ? (
                    <div 
                        className="rounded p-2.5 shadow-sm"
                        style={{ 
                            backgroundColor: colors.redSoft,
                            borderLeft: `4px solid ${colors.redCritical}`
                        }}
                    >
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold mb-0.5" style={{ color: colors.redCritical }}>
                            <AlertTriangle size={12} /> Capacidade Insuficiente
                        </div>
                        <div className="text-sm font-bold" style={{ color: colors.grayText }}>
                            Gargalo: {factoryMetrics.bottleneckName}
                        </div>
                        <div className="flex justify-between mt-1.5 text-xs" style={{ color: colors.grayText, opacity: 0.8 }}>
                            <span>CT gargalo: <b style={{ color: colors.redCritical }}>{factoryMetrics.bottleneckCycleTime.toFixed(1)} min</b></span>
                        </div>
                        <div className="flex justify-between mt-0.5 text-xs" style={{ color: colors.grayText, opacity: 0.8 }}>
                            <span>Takt: <b>{factoryMetrics.taktTime.toFixed(1)} min</b></span>
                            <span>Excede: <b style={{ color: colors.redCritical }}>+{factoryMetrics.bottleneckGapPercent.toFixed(0)}%</b></span>
                        </div>
                    </div>
                ) : isFactoryMap ? (
                    <div className="rounded p-2.5 text-center shadow-sm bg-emerald-50 border border-emerald-200">
                        <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-emerald-600">
                            <CheckCircle2 size={14} /> {isFuturo ? 'Fluxo Balanceado' : 'Capacidade OK'}
                        </div>
                        <div className="text-[9px] text-emerald-600 mt-0.5">
                            {isFuturo
                                ? `Capacidade Disponível: +${factoryMetrics.capacityAvailablePercent}%`
                                : factoryMetrics.taktTime > 0 
                                ? `CT gargalo <= Takt (${factoryMetrics.taktTime.toFixed(1)} min)`
                                : 'Configure demanda do cliente para takt'}
                        </div>
                    </div>
                ) : !dashboardMetrics.isFlowBalanced && dashboardMetrics.taktTime > 0 ? (
                    <div 
                        className="rounded p-2.5 shadow-sm"
                        style={{ 
                            backgroundColor: colors.redSoft,
                            borderLeft: `4px solid ${colors.redCritical}`
                        }}
                    >
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold mb-0.5" style={{ color: colors.redCritical }}>
                            <AlertTriangle size={12} /> Fluxo Desbalanceado
                        </div>
                        <div className="text-sm font-bold" style={{ color: colors.grayText }}>
                            Gargalo: {dashboardMetrics.bottleneckName || 'Não identificado'}
                        </div>
                        <div className="flex justify-between mt-1.5 text-xs" style={{ color: colors.grayText, opacity: 0.8 }}>
                            <span>Etapas excedendo Takt: <b style={{ color: colors.redCritical }}>{dashboardMetrics.cardsExceedingTakt}</b></span>
                        </div>
                        <div className="flex justify-between mt-0.5 text-xs" style={{ color: colors.grayText, opacity: 0.8 }}>
                            <span>Takt: <b>{dashboardMetrics.taktTime.toFixed(1)} min</b></span>
                            <span>Excede: <b style={{ color: colors.redCritical }}>+{dashboardMetrics.bottleneckExceedPercent?.toFixed(0) || 0}%</b></span>
                        </div>
                    </div>
                ) : dashboardMetrics.hasExcessiveIdleness && dashboardMetrics.taktTime > 0 ? (
                    <div className="rounded p-2.5 text-center shadow-sm bg-amber-50 border border-amber-200">
                        <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-amber-600">
                            <AlertTriangle size={14} /> Ociosidade Excessiva
                        </div>
                        <div className="text-[9px] text-amber-600 mt-0.5">
                            Todas etapas &lt; 50% do Takt ({dashboardMetrics.taktTime.toFixed(0)}min)
                        </div>
                    </div>
                ) : (
                    <div className="rounded p-2.5 text-center shadow-sm bg-emerald-50 border border-emerald-200">
                        <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-emerald-600">
                            <CheckCircle2 size={14} /> Fluxo Balanceado
                        </div>
                        <div className="text-[9px] text-emerald-600 mt-0.5">
                            {dashboardMetrics.taktTime > 0 
                                ? `Todas etapas â‰¤ Takt (${dashboardMetrics.taktTime.toFixed(0)}min)`
                                : 'Configure demanda diária para análise'}
                        </div>
                    </div>
                )}

                {/* 2. CUSTO */}
                <div className={`rounded p-2.5 shadow-sm border ${dashboardMetrics.hasOverflowCost ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-100'}`}>
                    <div className="flex justify-between items-start">
                        <div className={`text-[10px] font-semibold flex items-center gap-1 ${dashboardMetrics.hasOverflowCost ? 'text-red-700' : 'text-emerald-700'}`}>
                            <DollarSign size={12}/> {isFactoryMap ? 'Capacidade Gargalo' : 'Custo Estimado'}
                        </div>
                        <span className={`text-[9px] px-1 rounded font-semibold text-white ${dashboardMetrics.hasOverflowCost ? 'bg-red-600' : 'bg-emerald-600'}`}>MENSAL</span>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xl font-bold mt-0.5 ${dashboardMetrics.hasOverflowCost ? 'text-red-600' : 'text-slate-700'}`}>
                        {/* Exibe "Erro de Entrada" se houver overflow, senão formata o valor */}
                        {dashboardMetrics.hasOverflowCost 
                            ? 'Erro de Entrada'
                            : isFactoryMap
                            ? <><span>{factoryMetrics.capacityPerDay.toLocaleString('pt-BR')} un/dia</span>{renderImpactBadge(factoryMetrics.capacityDeltaPerDay, ' un/dia', 'increase', 0)}</>
                            : `R$ ${(dashboardMetrics.cost).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                    </div>
                    {dashboardMetrics.hasOverflowCost && (
                        <div className="text-[9px] text-red-500 mt-0.5">Verifique os dados de entrada</div>
                    )}
                </div>
                
                {/* 2.5 CONTAGEM DE ATIVIDADES */}
                <div className="rounded p-2.5 bg-blue-50 shadow-sm border border-blue-100">
                    <div className="text-[10px] font-semibold flex items-center gap-1 text-blue-700 mb-2">
                        <GitBranch size={12}/> {isFactoryMap ? 'Composição VSM' : 'Composio do Fluxo'}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-slate-700">
                                {isFactoryMap ? factoryMetrics.processCount : cards.filter(c => c.tipo === 'atividade').length}
                            </div>
                            <div className="text-[9px] text-slate-500 font-medium">{isFactoryMap ? 'Processos' : 'Atividades'}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-slate-700">
                                {isFactoryMap ? factoryMetrics.inventoryCount : cards.filter(c => c.tipo === 'decisao').length}
                            </div>
                            <div className="text-[9px] text-slate-500 font-medium">{isFactoryMap ? 'Estoques' : 'Decises'}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                {isFactoryMap ? factoryMetrics.communicationCount : metrics.totalPossiblePaths || 1}
                            </div>
                            <div className="text-[9px] text-blue-600 font-bold">{isFactoryMap ? 'Infos' : 'Caminhos'}</div>
                        </div>
                    </div>
                </div>

                {/* 3. ANLISE DE FLUXO */}
                <div className="rounded p-2.5 bg-white shadow-sm border border-slate-100">
                    <h4 className="text-[10px] font-semibold mb-2 pb-1.5 border-b text-slate-400">
                        {isFactoryMap ? 'Análise VSM' : 'Anlise de Fluxo'}
                    </h4>
                    <div className="space-y-2">
                        <button 
                            onClick={() => setHighlight(metrics.longestPathIds)} 
                            className="w-full rounded p-1.5 text-left hover:brightness-[1.03] transition-all bg-red-50 border border-red-100"
                        >
                            <div className="flex items-center gap-1.5 text-xs mb-1 text-red-600 font-bold">
                                <TrendingDown size={12}/> {isFactoryMap ? 'Lead Time Total' : 'Crítico (Longo)'}
                            </div>
                            {(() => {
                              const tp = formatTP(Number(metrics.longestPath) || 0);
                              return <div className="flex items-center gap-1.5 text-sm font-bold mb-1 text-slate-700">{isFactoryMap ? <><span>{factoryMetrics.leadTimeDays.toFixed(1)} d</span>{renderImpactBadge(factoryMetrics.leadTimeDeltaDays, 'd', 'decrease')}</> : `${tp.value} ${tp.unit}`}</div>;
                            })()}
                            <div className="grid grid-cols-3 gap-1.5 text-[9px] text-slate-500">
                                <div><span className="font-semibold">{isFactoryMap ? 'VA:' : 'P:'}</span> {isFactoryMap ? formatFactoryVa(factoryMetrics.totalCycleTime) : metrics.longestPathP || '0'}</div>
                                {(() => {
                                  const tp = formatTP(Number(metrics.longestPathTP) || 0);
                                  return <div><span className="font-semibold">{isFactoryMap ? 'Est:' : 'TP:'}</span> {isFactoryMap ? `${factoryMetrics.inventoryDays.toFixed(1)}d` : `${tp.value}${tp.unit}`}</div>;
                                })()}
                                {(() => {
                                  const tra = formatTRA(Number(metrics.longestPathTRA) || 0);
                                  return <div><span className="font-semibold">{isFactoryMap ? 'Takt:' : 'TRA:'}</span> {isFactoryMap ? `${factoryMetrics.taktTime.toFixed(1)}m` : `${tra.value}${tra.unit}`}</div>;
                                })()}
                            </div>
                        </button>
                        <button 
                            onClick={() => setHighlight(metrics.shortestPathIds)} 
                            className="w-full rounded p-1.5 text-left hover:brightness-[1.03] transition-all bg-emerald-50 border border-emerald-100"
                        >
                            <div className="flex items-center gap-1.5 text-xs mb-1 text-emerald-600 font-bold">
                                <TrendingUp size={12}/> {isFactoryMap ? 'Disponibilidade' : 'Rpido (Curto)'}
                            </div>
                            {(() => {
                              const tp = formatTP(Number(metrics.shortestPath) || 0);
                              return <div className="text-sm font-bold mb-1 text-slate-700">{isFactoryMap ? `${factoryMetrics.avgAvailability.toFixed(1)} %` : `${tp.value} ${tp.unit}`}</div>;
                            })()}
                            <div className="grid grid-cols-3 gap-1.5 text-[9px] text-slate-500">
                                <div><span className="font-semibold">{isFactoryMap ? 'Disp:' : 'P:'}</span> {isFactoryMap ? `${factoryMetrics.avgAvailability.toFixed(1)}%` : metrics.shortestPathP || '0'}</div>
                                {(() => {
                                  const tp = formatTP(Number(metrics.shortestPathTP) || 0);
                                  return <div><span className="font-semibold">{isFactoryMap ? 'Cap:' : 'TP:'}</span> {isFactoryMap ? `${factoryMetrics.capacityPerDay}un` : `${tp.value}${tp.unit}`}</div>;
                                })()}
                                {(() => {
                                  const tra = formatTRA(Number(metrics.shortestPathTRA) || 0);
                                  return <div><span className="font-semibold">{isFactoryMap ? 'Dem:' : 'TRA:'}</span> {isFactoryMap ? `${factoryMetrics.dailyDemand.toFixed(0)}un` : `${tra.value}${tra.unit}`}</div>;
                                })()}
                            </div>
                        </button>
                    </div>
                    <button 
                        onClick={() => setHighlight(null)} 
                        className="w-full text-center text-[9px] py-1.5 mt-2 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                    >
                        <RotateCcw size={10}/> Limpar Destaque
                    </button>
                </div>

                {/* 4. KPI GRID */}
                <div className="grid grid-cols-2 gap-2">
                    <KPICardCompact 
                        label={isFactoryMap ? 'Capacidade Gargalo' : 'Potencial de Entrega'} 
                        value={
                            isFactoryMap
                            ? factoryMetrics.capacityPerDay
                            : dashboardMetrics.resourceRanking.length > 0 
                            ? Math.floor(480 / Math.max(dashboardMetrics.resourceRanking[0].tra, 1)) 
                            : 0
                        } 
                        unit="un/dia" 
                    />
                    <KPICardCompact 
                        label={isFactoryMap ? 'Demanda Cliente' : 'Backlog'} 
                        value={isFactoryMap ? factoryMetrics.dailyDemand.toFixed(0) : metrics.backlog} 
                        unit="un/dia"
                        color={metrics.backlog > 0 ? colors.redCritical : colors.greenLean}
                    />
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------
    // RENDERIZA!ÑO: MODO FULLSCREEN (Cockpit Executivo)
    // ---------------------------------------------------------
    return (
        <div className="dashboard-expanded h-full bg-slate-50 dark:bg-slate-900 flex flex-col overflow-hidden font-sans">
            {/* Filtro Banner Premium */}
            {selectedResp && (
                <div className="bg-indigo-600 dark:bg-indigo-700 text-white px-8 py-2.5 text-sm font-semibold flex justify-between items-center shadow-sm z-30 border-b border-indigo-500">
                    <span>Análise Focada — {selectedResp}</span>
                    <button
                      onClick={() => {
                        setSelectedResp(null);
                        setHighlight(null);
                        onClearMapFilters?.();
                      }}
                      className="hover:text-indigo-200 transition-colors px-3 py-1 rounded hover:bg-white/10"
                    >
                        Limpar Filtro
                    </button>
                </div>
            )}

            {/* Conteúdo Principal */}
            <div className="flex-1 overflow-y-auto p-3.5 pb-6 dark:bg-slate-900 [background-image:radial-gradient(rgba(148,163,184,0.22)_0.6px,transparent_0.6px)] [background-size:14px_14px] dark:[background-image:radial-gradient(rgba(82,82,91,0.18)_0.6px,transparent_0.6px)]">
                    <div className="flex flex-col gap-3">
                        {/* 1. COCKPIT EXECUTIVO - KPIs Premium (Ajustado para altura fixa) */}
                        <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3 shadow-sm border border-slate-200 dark:border-slate-700 shrink-0">
                            <div className="flex items-center gap-2.5 mb-2.5">
                                <div className="h-7 w-7 inline-flex items-center justify-center bg-indigo-600 rounded-md">
                                    <Gauge className="text-white" size={16}/>
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">
                                        {isFactoryMap ? 'Cockpit Factory VSM' : 'Cockpit Executivo'}
                                    </h2>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                                        {isFactoryMap ? 'Takt, capacidade, lead time e qualidade do fluxo produtivo' : 'Indicadores Estrat\u00e9gicos em Tempo Real'}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                                {(() => {
                                  const tp = formatTP(dashboardMetrics.totalTP);
                                  return (
                                    <div className="bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-all min-h-[132px] flex flex-col">
                                        <div className="flex items-center gap-2 mb-2.5">
                                            <div className="h-8 w-8 inline-flex items-center justify-center bg-indigo-50 dark:bg-indigo-950/50 rounded">
                                                <Clock size={16} className="text-indigo-600 dark:text-indigo-400"/>
                                            </div>
                                        </div>
                                        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{isFactoryMap ? 'Lead Time VSM' : 'Lead Time'}</div>
                                        <div className="flex items-baseline gap-1 mb-0.5">
                                            <span className="text-2xl font-medium font-mono text-slate-800 dark:text-slate-100">{isFactoryMap ? factoryMetrics.leadTimeDays.toFixed(1) : tp.value}</span>
                                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{isFactoryMap ? 'd' : tp.unit}</span>
                                            {isFactoryMap && renderImpactBadge(factoryMetrics.leadTimeDeltaDays, 'd', 'decrease')}
                                        </div>
                                        <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">{isFactoryMap ? `Est: ${factoryMetrics.inventoryDays.toFixed(1)}d | VA: ${formatFactoryVa(factoryMetrics.totalCycleTime)}` : 'Tempo total perman\u00eancia'}</div>
                                    </div>
                                  );
                                })()}

                                <div className="bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-all min-h-[132px] flex flex-col">
                                    <div className="flex items-center justify-between mb-2.5">
                                        <div className="h-8 w-8 inline-flex items-center justify-center bg-emerald-50 dark:bg-emerald-950/50 rounded">
                                            <Activity size={16} className={(isFactoryMap ? factoryMetrics.pce : dashboardMetrics.efficiency) < 15 ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}/>
                                        </div>
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${
                                            (isFactoryMap ? factoryMetrics.pce : dashboardMetrics.efficiency) > 20 ? 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300' : 'bg-rose-100 dark:bg-rose-950/70 text-rose-700 dark:text-rose-300'
                                        }`}>
                                            {(isFactoryMap ? factoryMetrics.pce : dashboardMetrics.efficiency) > 20 ? '\u2191' : '\u2193'}
                                        </span>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{'Efici\u00eancia PCE'}</div>
                                    <div className="flex items-baseline gap-1 mb-0.5">
                                        <span className="text-2xl font-medium font-mono text-slate-800 dark:text-slate-100">{isFactoryMap ? formatFactoryPce(factoryMetrics.pce) : dashboardMetrics.efficiency.toFixed(1)}</span>
                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">%</span>
                                    </div>
                                    <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">{isFactoryMap ? `VA: ${formatFactoryVa(factoryMetrics.totalCycleTime)} sobre LT total` : 'Valor vs Espera'}</div>
                                </div>

                                <div className={`bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border ${dashboardMetrics.hasOverflowCost ? 'border-red-300 dark:border-red-700' : 'border-slate-100 dark:border-slate-700'} hover:shadow-lg transition-all min-h-[132px] flex flex-col`}>
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div className={`h-8 w-8 inline-flex items-center justify-center rounded ${dashboardMetrics.hasOverflowCost ? 'bg-red-50 dark:bg-red-950/50' : 'bg-teal-50 dark:bg-teal-950/50'}`}>
                                            <DollarSign size={16} className={dashboardMetrics.hasOverflowCost ? 'text-red-600 dark:text-red-400' : 'text-teal-600 dark:text-teal-400'}/>
                                        </div>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{isFactoryMap ? 'Capacidade Gargalo' : 'Custo Operacional'}</div>
                                    <div className="mb-0.5 flex items-center gap-1.5">
                                        <span className={`text-2xl font-medium font-mono ${dashboardMetrics.hasOverflowCost ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                            {isFactoryMap
                                                ? factoryMetrics.capacityPerDay.toLocaleString('pt-BR')
                                                : dashboardMetrics.hasOverflowCost 
                                                ? 'Erro de entrada' 
                                                : dashboardMetrics.cost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                                        </span>
                                            {isFactoryMap && renderImpactBadge(factoryMetrics.capacityDeltaPerDay, ' un/dia', 'increase', 0)}
                                    </div>
                                    <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">
                                        {isFactoryMap ? isFuturo ? `Capacidade Disponível: +${factoryMetrics.capacityAvailablePercent}%` : 'un/dia no maior CT' : dashboardMetrics.hasOverflowCost ? 'Verifique os dados' : 'Custo Hora/TRA'}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-all min-h-[132px] flex flex-col">
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div className="h-8 w-8 inline-flex items-center justify-center bg-amber-50 dark:bg-amber-950/50 rounded">
                                            <Zap size={16} className="text-amber-600 dark:text-amber-400"/>
                                        </div>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{isFactoryMap ? 'Takt Cliente' : 'Velocidade'}</div>
                                    <div className="flex items-baseline gap-1 mb-0.5">
                                        <span className="text-2xl font-medium font-mono text-slate-800 dark:text-slate-100">{isFactoryMap ? factoryMetrics.taktTime.toFixed(1) : dashboardMetrics.flowVelocity.toFixed(1)}</span>
                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{isFactoryMap ? 'min/un' : 'it/h'}</span>
                                    </div>
                                    <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">{isFactoryMap ? `${factoryMetrics.dailyDemand.toFixed(0)} un/dia` : 'Itens processados/hora'}</div>
                                </div>

                                <div className="bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-all min-h-[132px] flex flex-col">
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div className={`h-8 w-8 inline-flex items-center justify-center rounded ${isFactoryMap && (isFuturo || (isKaizenWork && factoryMetrics.canMeetDemand)) ? 'bg-emerald-50 dark:bg-emerald-950/50' : 'bg-rose-50 dark:bg-rose-950/50'}`}>
                                            {isFactoryMap && (isFuturo || (isKaizenWork && factoryMetrics.canMeetDemand)) ? <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400"/> : <AlertTriangle size={16} className="text-rose-600 dark:text-rose-400"/>}
                                        </div>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{isFactoryMap ? isFuturo ? 'Fluxo Balanceado' : isKaizenWork ? factoryMetrics.canMeetDemand ? 'Gargalo Solucionado' : 'Otimização em Andamento' : 'CT Gargalo' : 'Custo Desperd\u00edcio'}</div>
                                    <div className="flex items-baseline gap-1 mb-0.5">
                                        <span className={`text-2xl font-medium font-mono ${isFactoryMap && (isFuturo || (isKaizenWork && factoryMetrics.canMeetDemand)) ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                            {isFactoryMap ? formatFactoryCycle(factoryMetrics.bottleneckCycleTime) : dashboardMetrics.monthlyWasteCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                                        </span>
                                    </div>
                                    <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">
                                        {isFactoryMap ? factoryMetrics.bottleneckName : `${dashboardMetrics.dailyWasteCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}/dia`}
                                    </div>
                                </div>

                                {/* Card RTY - Rolled Throughput Yield */}
                                <div className="bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-all min-h-[132px] flex flex-col">
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div className="h-8 w-8 inline-flex items-center justify-center bg-emerald-50 dark:bg-emerald-950/50 rounded">
                                            <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400"/>
                                        </div>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{isFactoryMap ? 'Disponibilidade' : 'RTY — First Pass'}</div>
                                    <div className="flex items-baseline gap-1 mb-0.5">
                                        <span className={`text-2xl font-medium font-mono ${dashboardMetrics.rty >= 85 ? 'text-emerald-600 dark:text-emerald-400' : dashboardMetrics.rty >= 70 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                            {(isFactoryMap ? factoryMetrics.avgAvailability : dashboardMetrics.rty).toFixed(1)}
                                        </span>
                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">%</span>
                                    </div>
                                    <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">{isFactoryMap ? 'Média dos processos' : 'Yield acumulado'}</div>
                                </div>
                                
                                {/* Card de Composição do Fluxo */}
                                <div className="bg-white dark:bg-slate-800 rounded p-3.5 shadow-md border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-all min-h-[132px] flex flex-col">
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div className="h-8 w-8 inline-flex items-center justify-center bg-blue-50 dark:bg-blue-950/50 rounded">
                                            <GitBranch size={16} className="text-blue-600 dark:text-blue-400"/>
                                        </div>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-0.5">{isFactoryMap ? 'Composição VSM' : 'Composi\u00e7\u00e3o'}</div>
                                    <div className="flex items-baseline gap-1 mb-0.5">
                                        <span className="text-2xl font-medium font-mono text-slate-800 dark:text-slate-100">
                                            {isFactoryMap ? factoryMetrics.processCount : cards.filter(c => c.tipo === 'atividade' || c.tipo === 'decisao').length}
                                        </span>
                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{isFactoryMap ? 'proc.' : 'nodes'}</span>
                                    </div>
                                    <div className="mt-auto pt-1 text-[9px] text-slate-500 dark:text-slate-500 font-medium">
                                        {isFactoryMap ? `${factoryMetrics.inventoryCount} estoques + ${factoryMetrics.transportCount} transportes` : `${cards.filter(c => c.tipo === 'atividade').length} ativ. + ${cards.filter(c => c.tipo === 'decisao').length} dec.`}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. ANLISE OPERACIONAL Premium */}
                        <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
                            {/* PARETO COM BARRAS DE CARGA */}
                            <div className="col-span-12 lg:col-span-7">
                                <div className="bg-white/95 dark:bg-slate-800 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 h-full flex flex-col">
                                    <div className="p-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
                                        <div className="flex items-center gap-2.5">
                                            <div className="h-7 w-7 inline-flex items-center justify-center bg-purple-600 rounded-md">
                                                <BarChart3 className="text-white" size={16}/>
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">{isFactoryMap ? 'Yamazumi / Capacidade' : 'An\u00e1lise de Ocupa\u00e7\u00e3o'}</h3>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{isFactoryMap ? 'CT por processo comparado ao takt' : 'Potencial vs Demanda por Recurso'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-1 p-3 overflow-hidden">
                                        <YamazumiChart 
                                            data={isFactoryMap ? factoryMetrics.factoryYamazumiData : dashboardMetrics.yamazumiData} 
                                            onSelect={setSelectedResp} 
                                            selectedItem={selectedResp}
                                            taktTime={isFactoryMap ? factoryMetrics.taktTime : dashboardMetrics.taktTime}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* QUALITY & INSIGHTS */}
                            <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
                                {/* Card OEE Premium */}
                                <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3 shadow-sm border border-slate-200 dark:border-slate-700 shrink-0">
                                    <div className="flex items-center gap-2.5 mb-2.5">
                                        <div className="h-7 w-7 inline-flex items-center justify-center bg-purple-600 rounded-md">
                                            <Target className="text-white" size={16}/>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">{isFactoryMap ? 'Estabilidade Produtiva' : 'Efic\u00e1cia Operacional'}</h3>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{isFactoryMap ? 'Disponibilidade e aderência ao takt' : 'Desempenho Geral do Processo'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-end gap-2 mb-2">
                                        <span className="text-4xl font-bold font-mono bg-gradient-to-r from-purple-600 to-purple-700 dark:from-purple-400 dark:to-purple-500 bg-clip-text text-transparent">
                                            {(isFactoryMap ? factoryMetrics.avgAvailability : dashboardMetrics.oee).toFixed(1)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-200 dark:bg-slate-700 h-2.5 rounded-full overflow-hidden shadow-inner mb-3">
                                        <div className="h-full bg-gradient-to-r from-purple-500 to-purple-600 shadow-lg transition-all duration-500" 
                                             style={{ width: `${isFactoryMap ? factoryMetrics.avgAvailability : dashboardMetrics.oee}%` }}/>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-center">
                                        <div className="p-1.5 bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-100 dark:border-slate-700">
                                            <div className="text-[8px] text-slate-500 dark:text-slate-400 font-bold uppercase mb-0.5">Disponib.</div>
                                            <div className="text-base font-bold text-slate-700 dark:text-slate-200">{(isFactoryMap ? factoryMetrics.avgAvailability : 95).toFixed(0)}%</div>
                                        </div>
                                        <div className="p-1.5 bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-100 dark:border-slate-700">
                                            <div className="text-[8px] text-slate-500 dark:text-slate-400 font-bold uppercase mb-0.5">PCE</div>
                                            <div className="text-base font-bold text-slate-700 dark:text-slate-200">{isFactoryMap ? `${formatFactoryPce(factoryMetrics.pce)}%` : `${dashboardMetrics.efficiency.toFixed(0)}%`}</div>
                                        </div>
                                        <div className="p-1.5 bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-100 dark:border-slate-700">
                                            <div className="text-[8px] text-slate-500 dark:text-slate-400 font-bold uppercase mb-0.5">Takt OK</div>
                                            <div className="text-base font-bold text-slate-700 dark:text-slate-200">{isFactoryMap ? (factoryMetrics.canMeetDemand ? 'Sim' : 'Não') : '98%'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Alertas Premium */}
                                <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3 shadow-sm border border-slate-200 dark:border-slate-700 flex-1 min-h-0 flex flex-col">
                                    <div className="flex items-center gap-2.5 mb-2.5 shrink-0">
                                        <div className="h-7 w-7 inline-flex items-center justify-center bg-amber-600 rounded-md">
                                            <Bell className="text-white" size={16}/>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">{'Insights Estrat\u00e9gicos'}</h3>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{'Recomenda\u00e7\u00f5es do Consultor'}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-2 overflow-y-auto custom-scrollbar flex-1 pr-0.5">
                                        {strategicInsights.map((insight) => {
                                            const toneClasses =
                                                insight.tone === 'rose'
                                                    ? {
                                                          border: 'border-rose-500',
                                                          badge: 'text-rose-700 bg-rose-50 border-rose-200',
                                                      }
                                                    : insight.tone === 'amber'
                                                      ? {
                                                            border: 'border-amber-500',
                                                            badge: 'text-amber-700 bg-amber-50 border-amber-200',
                                                        }
                                                      : {
                                                            border: 'border-slate-500',
                                                            badge: 'text-slate-700 bg-slate-100 border-slate-200',
                                                        };

                                            return (
                                                <div
                                                    key={insight.id}
                                                    className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 border-l-4 ${toneClasses.border} rounded-md p-2.5`}
                                                >
                                                    <div className="flex items-start justify-between gap-2 mb-1.5">
                                                        <span className={`text-[11px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${toneClasses.badge}`}>
                                                            {insight.badge}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 mb-1">
                                                        {insight.title}
                                                    </div>
                                                    <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                                        {insight.body}
                                                    </div>
                                                    <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mt-1.5">
                                                        {insight.suggestion}
                                                    </div>
                                                    {insight.focusResource && (
                                                        <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSimulateInsightSolution(insight.focusResource)}
                                                                className="inline-flex items-center gap-1 rounded-sm border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                                            >
                                                                <Play size={12} strokeWidth={1.5} />
                                                                {'Simular Solução'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {isFactoryMap && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3.5 shadow-sm border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2.5 mb-3">
                                        <div className="h-7 w-7 inline-flex items-center justify-center bg-cyan-600 rounded-md">
                                            <Layers className="text-white" size={16}/>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">Inventário e Lead Time</h3>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Estoques que sustentam o PLT do VSM</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 p-2">
                                            <div className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">Estoque Total</div>
                                            <div className="text-xl font-medium font-mono text-slate-800 dark:text-slate-100">{factoryMetrics.inventoryUnits.toLocaleString('pt-BR')}</div>
                                            <div className="text-[9px] text-slate-500">unidades</div>
                                        </div>
                                        <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 p-2">
                                            <div className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">Espera</div>
                                            <div className="text-xl font-medium font-mono text-slate-800 dark:text-slate-100">{factoryMetrics.inventoryDays.toFixed(1)}</div>
                                            <div className="text-[9px] text-slate-500">dias</div>
                                        </div>
                                        <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 p-2">
                                            <div className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">Valor Agregado</div>
                                            <div className="text-xl font-medium font-mono text-slate-800 dark:text-slate-100">{formatFactoryVa(factoryMetrics.totalCycleTime)}</div>
                                            <div className="text-[9px] text-slate-500">VA</div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {factoryMetrics.inventoryBreakdown.slice(0, 4).map((item, index) => (
                                            <div key={`${item.name}-${index}`} className="flex items-center gap-2 text-xs">
                                                <div className="w-28 truncate font-semibold text-slate-600 dark:text-slate-300" title={item.name}>{item.name}</div>
                                                <div className="flex-1 h-2 rounded-sm bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                                    <div className="h-full bg-cyan-500" style={{ width: `${factoryMetrics.inventoryDays > 0 ? Math.min(100, (item.days / factoryMetrics.inventoryDays) * 100) : 0}%` }} />
                                                </div>
                                                <div className="w-16 text-right font-mono text-slate-700 dark:text-slate-200">{item.days.toFixed(1)}d</div>
                                            </div>
                                        ))}
                                        {factoryMetrics.inventoryBreakdown.length === 0 && (
                                            <div className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">Adicione estoques com dias ou quantidade para avaliar o PLT.</div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3.5 shadow-sm border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2.5 mb-3">
                                        <div className="h-7 w-7 inline-flex items-center justify-center bg-emerald-600 rounded-md">
                                            <Target className="text-white" size={16}/>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">Capacidade vs Demanda</h3>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Aderência do gargalo ao takt do cliente</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 p-2">
                                            <div className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">Capacidade</div>
                                            <div className="text-2xl font-medium font-mono text-slate-800 dark:text-slate-100">{factoryMetrics.capacityPerDay.toLocaleString('pt-BR')}</div>
                                            <div className="text-[9px] text-slate-500">un/dia no gargalo</div>
                                        </div>
                                        <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 p-2">
                                            <div className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mb-1">Demanda</div>
                                            <div className="text-2xl font-medium font-mono text-slate-800 dark:text-slate-100">{factoryMetrics.dailyDemand.toFixed(0)}</div>
                                            <div className="text-[9px] text-slate-500">un/dia cliente</div>
                                        </div>
                                    </div>
                                    <div className={`rounded-sm border p-3 ${factoryMetrics.canMeetDemand ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800' : 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800'}`}>
                                        <div className={`text-sm font-bold mb-1 ${factoryMetrics.canMeetDemand ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                                            {factoryMetrics.canMeetDemand ? isFuturo ? 'Fluxo Balanceado' : isKaizenWork ? 'Gargalo Solucionado' : 'Fluxo atende ao takt' : isKaizenWork ? 'Otimização em Andamento' : `Gargalo excede takt em ${factoryMetrics.bottleneckGapPercent.toFixed(0)}%`}
                                        </div>
                                        <div className="text-xs text-slate-600 dark:text-slate-300">
                                            {isFuturo
                                                ? `Capacidade Disponível: +${factoryMetrics.capacityAvailablePercent}%. Célula em 18.3s/Op, takt ${factoryMetrics.taktTime.toFixed(1)} min/un.`
                                                : isKaizenWork
                                                ? `${factoryMetrics.bottleneckName}: CT ${formatFactoryCycle(factoryMetrics.bottleneckCycleTime)}, takt ${factoryMetrics.taktTime.toFixed(1)} min/un. Capacidade ${factoryMetrics.capacityPerDay.toLocaleString('pt-BR')} un/dia.`
                                                : `${factoryMetrics.bottleneckName}: CT ${factoryMetrics.bottleneckCycleTime.toFixed(1)} min, takt ${factoryMetrics.taktTime.toFixed(1)} min/un.`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isFactoryMap && (
                            <>
                        {/* SIMULA!ÑO MONTE CARLO */}
                        <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3.5 shadow-sm border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2.5 mb-3">
                                <div className="h-7 w-7 inline-flex items-center justify-center bg-purple-600 rounded-md">
                                    <BarChart3 className="text-white" size={16}/>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">{'Simula\u00e7\u00e3o Monte Carlo'}</h3>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{'An\u00e1lise probabil\u00edstica do Lead Time'}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
                                {/* Percentil 10% (Otimista) */}
                                <div className="bg-white dark:bg-slate-900 rounded-md p-2.5 border border-slate-200 dark:border-slate-700 shadow-sm">
                                    <div className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 mb-1.5">P10 — Otimista</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-2xl font-bold font-mono text-slate-700 dark:text-slate-200">
                                            {monteCarloResult ? monteCarloResult.p10LeadTime.toFixed(1) : (dashboardMetrics.totalTP * 0.75).toFixed(1)}
                                        </span>
                                        <span className="text-xs text-emerald-600 dark:text-emerald-400">min</span>
                                    </div>
                                    <div className="text-[9px] text-slate-500 dark:text-slate-500 mt-1">10% dos casos</div>
                                </div>
                                
                                {/* Percentil 50% (Mediano) */}
                                <div className="bg-white dark:bg-slate-900 rounded-md p-2.5 border border-emerald-300/70 dark:border-emerald-700/60 shadow-sm">
                                    <div className="text-[10px] font-semibold text-blue-700 dark:text-blue-400 mb-1.5">{'P50 — T\u00edpico'}</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-400">
                                            {monteCarloResult ? monteCarloResult.p50LeadTime.toFixed(1) : dashboardMetrics.totalTP.toFixed(1)}
                                        </span>
                                        <span className="text-xs text-blue-600 dark:text-blue-400">min</span>
                                    </div>
                                    <div className="text-[9px] text-slate-500 dark:text-slate-500 mt-1">50% (mediana)</div>
                                </div>
                                
                                {/* Percentil 90% (Pessimista) */}
                                <div className="bg-white dark:bg-slate-900 rounded-md p-2.5 border border-slate-200 dark:border-slate-700 shadow-sm">
                                    <div className="text-[10px] font-semibold text-orange-700 dark:text-orange-400 mb-1.5">P90 — Pessimista</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-2xl font-bold font-mono text-slate-700 dark:text-slate-200">
                                            {monteCarloResult ? monteCarloResult.p90LeadTime.toFixed(1) : (dashboardMetrics.totalTP * 1.35).toFixed(1)}
                                        </span>
                                        <span className="text-xs text-orange-600 dark:text-orange-400">min</span>
                                    </div>
                                    <div className="text-[9px] text-slate-500 dark:text-slate-500 mt-1">90% dos casos</div>
                                </div>
                                
                                {/* Desvio Padrão */}
                                <div className="bg-slate-50 dark:bg-slate-900 rounded-md p-2.5 border border-slate-300 dark:border-slate-700 shadow-sm">
                                    <div className="text-[10px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5">Variabilidade</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-2xl font-bold font-mono text-slate-700 dark:text-slate-200">
                                            {monteCarloResult ? monteCarloResult.stdDeviation.toFixed(1) : (dashboardMetrics.totalTP * 0.2).toFixed(1)}
                                        </span>
                                        <span className="text-xs text-purple-600 dark:text-purple-400">min</span>
                                    </div>
                                    <div className="text-[9px] text-slate-500 dark:text-slate-500 mt-1">{'Desvio padr\u00e3o'}</div>
                                </div>
                            </div>
                            
                            {/* Explicação (Progressive Disclosure) */}
                            <details className="mt-2.5 group">
                                <summary className="cursor-pointer select-none text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 inline-flex items-center gap-1">
                                    <ChevronDown size={12} className="transition-transform group-open:rotate-180" />
                                    Como interpretar a simulação
                                </summary>
                                <div className="mt-2 p-2.5 bg-slate-50 dark:bg-slate-900/50 rounded-md">
                                    <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                                        <b className="text-slate-800 dark:text-slate-200">Monte Carlo:</b> {'Simula\u00e7\u00e3o de 10.000 itera\u00e7\u00f5es com variabilidade de \u00b120% nos tempos. \u00datil para dimensionar buffers e prever prazos realistas.'}
                                    </p>
                                </div>
                            </details>
                        </div>
                        
                        {/* GRÁFICO DE GANTT - TIMELINE DO PROCESSO */}
                        <div className="bg-white/95 dark:bg-slate-800 rounded-md p-3.5 shadow-sm border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-2.5 mb-3">
                                <div className="h-7 w-7 inline-flex items-center justify-center bg-cyan-600 rounded-md">
                                    <BarChart3 className="text-white" size={16}/>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight">Timeline do Processo</h3>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{'Gantt por recurso com caminho cr\u00edtico'}</p>
                                </div>
                            </div>
                            
                            <div className="h-[300px]">
                                <GanttChart cards={cards} maxHeight={280} />
                            </div>
                        </div>
                            </>
                        )}
                    </div>
                </div>
            
            {/* Modal de Simulação de Cenário Futuro */}
            {showFutureModal && futureSimulation && (
                <FutureStateModal
                    isOpen={showFutureModal}
                    onClose={() => {
                      setShowFutureModal(false);
                      onClearMapFilters?.();
                    }}
                    currentMetrics={futureSimulation.currentMetrics}
                    futureMetrics={futureSimulation.futureMetrics}
                    improvements={futureSimulation.improvements}
                />
            )}

            {/* Modal de COMPARAÇÃO de Métricas */}
            {showComparisonModal && (() => {
                const { current, future } = getFutureMetrics();
                return (
                    <ComparisonView
                        isOpen={showComparisonModal}
                        onClose={() => {
                          setShowComparisonModal(false);
                          onClearMapFilters?.();
                        }}
                        onApplyMap={() => {
                          setShowComparisonModal(false);
                          onClearMapFilters?.();
                        }}
                        current={current}
                        future={future}
                    />
                );
            })()}
        </div>
    );
};


